import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Image as ImageIcon, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { imageApi } from '../api/api';

const IMAGE_SLOTS = [
  // Why Avopla Page
  { key: 'whyAvopla-header', label: 'Header Image', description: 'Hero image at the top of Why Avopla page', page: 'Why Avopla' },
  { key: 'whyAvopla-reachFoodies', label: 'Reach More Foodies', description: 'Mobile app image in the "Reach More Foodies" section', page: 'Why Avopla' },
  { key: 'whyAvopla-customerExperience', label: 'Customer Experience', description: 'Mobile app image in the "Customer Experience" section', page: 'Why Avopla' },
  { key: 'whyAvopla-controlReservations', label: 'Control Reservations', description: 'Dashboard image in the "Have Control" section', page: 'Why Avopla' },
  
  // Pricing Page
  { key: 'pricing-header', label: 'Header Image', description: 'Hero image at the top of Pricing page', page: 'Pricing' },
  
  // Registration Page
  { key: 'registration-attractFoodies', label: 'Attract More Foodies', description: 'Icon for "Attract more foodies" feature', page: 'Registration' },
  { key: 'registration-fullControl', label: 'Get Full Control', description: 'Icon for "Get full control" feature', page: 'Registration' },
  { key: 'registration-buildReputation', label: 'Build Your Reputation', description: 'Icon for "Build your reputation" feature', page: 'Registration' },
  
  // Customer Home Page
  { key: 'customer-homeBanner', label: 'Home Banner', description: 'Main banner image on customer home page', page: 'Customer Home' },
  { key: 'customer-homeMenu', label: 'Home Menu Icon', description: 'Menu icon in bottom section', page: 'Customer Home' },
  { key: 'customer-homeBanner2', label: 'Restaurant Owner Banner', description: 'Banner for restaurant owner section', page: 'Customer Home' },
  { key: 'customer-favIcon1', label: 'Favorite Icon (Active)', description: 'Favorite icon when restaurant is favorited', page: 'Customer Home' },
  { key: 'customer-favIcon0', label: 'Favorite Icon (Inactive)', description: 'Favorite icon when restaurant is not favorited', page: 'Customer Home' },
  { key: 'customer-upwardArrow', label: 'Upward Arrow', description: 'Arrow icon for navigation', page: 'Customer Home' },
  { key: 'customer-noImage', label: 'No Image Placeholder', description: 'Placeholder when restaurant has no image', page: 'Customer Home' },
  
  // Customer About Us Page
  { key: 'about-why1', label: 'Why Avopla - Explore & Enjoy', description: 'Icon for "Explore and enjoy" feature', page: 'About Us' },
  { key: 'about-why2', label: 'Why Avopla - Easy & Free', description: 'Icon for "Easy, free and instant" feature', page: 'About Us' },
  { key: 'about-why3', label: 'Why Avopla - Reviews', description: 'Icon for "Reviews and ratings" feature', page: 'About Us' },
  { key: 'about-why4', label: 'Why Avopla - Offers', description: 'Icon for "Offers and discounts" feature', page: 'About Us' },
  { key: 'about-rightImage', label: 'Restaurant Owner Image', description: 'Image for restaurant owner section at bottom', page: 'About Us' },
  { key: 'about-communityBg', label: 'Join Community Background', description: 'Background image for join community section', page: 'About Us' },
  
  // Customer Contact Us Page
  { key: 'contact-bottomImage', label: 'Restaurant Owner Dashboard CTA', description: 'Image for restaurant owner section at bottom', page: 'Contact Us' },
  
  // Customer Restaurant Detail Page
  { key: 'resto-locationIcon', label: 'Location Marker Icon', description: 'Map pin icon for restaurant location', page: 'Restaurant Detail' },
  { key: 'resto-districtIcon', label: 'District Icon', description: 'Icon for district/neighborhood list', page: 'Restaurant Detail' },
  { key: 'resto-tagIcon', label: 'Tag/Discount Icon', description: 'Icon for discount and offer tags', page: 'Restaurant Detail' },
  { key: 'resto-menuIcon', label: 'Menu Icon', description: 'Icon for menu tab/section', page: 'Restaurant Detail' },
  { key: 'resto-reviewIcon', label: 'Review Icon', description: 'Icon for reviews tab', page: 'Restaurant Detail' },
  { key: 'resto-placeholder1', label: 'Restaurant Image 1', description: 'Generic restaurant placeholder image 1', page: 'Restaurant Detail' },
  { key: 'resto-placeholder2', label: 'Restaurant Image 2', description: 'Generic restaurant placeholder image 2', page: 'Restaurant Detail' },
  { key: 'resto-placeholder3', label: 'Restaurant Image 3', description: 'Generic restaurant placeholder image 3', page: 'Restaurant Detail' },
  
  // Customer Restaurant List Page
  { key: 'restoList-noImage', label: 'No Image Placeholder', description: 'Placeholder image when restaurant has no photo', page: 'Restaurant List' },
];

const Images = () => {
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [activeTab, setActiveTab] = useState('Why Avopla');
  const fileInputRefs = useRef({});

  const pages = [...new Set(IMAGE_SLOTS.map(slot => slot.page))];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await imageApi.getAll();
      const imageMap = {};
      response.data.forEach(img => {
        imageMap[img.name] = img;
      });
      setImages(imageMap);
    } catch (error) {
      toast.error('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (slotKey, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const existingImage = images[slotKey];

    try {
      setUploading(slotKey);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', slotKey);
      formData.append('displayName', file.name.split('.')[0]);
      formData.append('section', slotKey);
      formData.append('altTextEn', '');
      formData.append('altTextFr', '');
      formData.append('description', '');

      if (existingImage) {
        await imageApi.replace(existingImage.id, formData);
        toast.success('Image replaced');
      } else {
        await imageApi.upload(formData);
        toast.success('Image uploaded');
      }

      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload');
    } finally {
      setUploading(null);
      if (fileInputRefs.current[slotKey]) {
        fileInputRefs.current[slotKey].value = '';
      }
    }
  };

  const handleDelete = async (slotKey) => {
    const image = images[slotKey];
    if (!image) return;
    if (!window.confirm('Delete this image?')) return;

    try {
      await imageApi.delete(image.id);
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentSlots = IMAGE_SLOTS.filter(slot => slot.page === activeTab);
  const uploadedCount = currentSlots.filter(slot => images[slot.key]).length;

  return (
    <div>
      <div className="page-header">
        <h1>Images</h1>
        <p>Manage images for each section of your website</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {pages.map(page => {
          const pageSlots = IMAGE_SLOTS.filter(s => s.page === page);
          const pageUploaded = pageSlots.filter(s => images[s.key]).length;

          return (
            <button
              key={page}
              onClick={() => setActiveTab(page)}
              className={`tab ${activeTab === page ? 'active' : ''}`}
            >
              {page}
              <span className="tab-badge">
                {pageUploaded}/{pageSlots.length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Progress */}
      <div className="progress-bar">
        <div className="progress-header">
          <span className="progress-label">{activeTab} Images</span>
          <span className="progress-value">{uploadedCount} of {currentSlots.length} uploaded</span>
        </div>
        <div className="progress-track">
          <div
            className={`progress-fill ${uploadedCount === currentSlots.length ? 'complete' : ''}`}
            style={{ width: `${(uploadedCount / currentSlots.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Image Slots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {currentSlots.map(slot => {
          const image = images[slot.key];
          const isUploading = uploading === slot.key;

          return (
            <div key={slot.key} className="image-slot">
              <div
                className="image-preview"
                onClick={() => fileInputRefs.current[slot.key]?.click()}
              >
                {isUploading ? (
                  <div style={{ textAlign: 'center' }}>
                    <div className="spinner" style={{ width: '24px', height: '24px', margin: '0 auto 8px' }}></div>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Uploading...</span>
                  </div>
                ) : image ? (
                  <img src={image.url} alt={image.displayName} />
                ) : (
                  <div className="image-preview-empty">
                    <ImageIcon size={24} />
                    <span>Click to upload</span>
                  </div>
                )}
                <input
                  ref={el => fileInputRefs.current[slot.key] = el}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileSelect(slot.key, e)}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="image-info">
                <h3>
                  {slot.label}
                  {image && <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />}
                </h3>
                <p>{slot.description}</p>
                <code>{slot.key}</code>
                <div className="image-actions">
                  {image ? (
                    <>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => fileInputRefs.current[slot.key]?.click()}
                      >
                        <RefreshCw size={14} />
                        Replace
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(slot.key)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => fileInputRefs.current[slot.key]?.click()}
                    >
                      <Upload size={14} />
                      Upload
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help */}
      <div className="alert alert-info" style={{ marginTop: '20px' }}>
        <AlertCircle size={18} className="alert-icon" />
        <div className="alert-content">
          <h4>Supported formats</h4>
          <div>JPG, PNG, GIF, SVG, and WebP files up to 10MB</div>
        </div>
      </div>
    </div>
  );
};

export default Images;
